/**
 * Pestaña Asistencia (006 · T032 · US2; contracts/staff-ui.md §4, spec US2).
 *
 * 60 días de marcas vigentes agrupadas por día operativo, del más reciente al más antiguo, con
 * "(ajustada)", "Observada"/"Sin salida registrada" y horas brutas cuando el par está completo. Sin
 * ninguna acción sobre las marcas (FR-008): es una consulta de solo lectura. Carga por tramos con
 * "Ver días anteriores" (`useHistory`, `next_cursor` opaco) — sin scroll infinito (R-7, accesible con
 * teclado/lector).
 *
 * Estados: `HistoryLoading` (skeletons, `history-loading`) mientras llega la primera página —también
 * la usa la puerta de `routes/asistencia.tsx` mientras resuelve `useAccessGate`—, `OfflineState` sin
 * red y sin nada cargado todavía, `OfflineBanner` sin red con datos ya en memoria (nunca persistidos,
 * wiring 006 §0.7), `GenericErrorScreen` ante un error y el vacío amable (`history-empty`) sin
 * ninguna marca en la ventana.
 *
 * 007 · T036/T057 (contracts/ui.md §1.3): arriba, la sección "Regularizaciones" (solo si hay
 * ítems) y, bajo la marca ancla de cada día, "Regularizar" o el estado. Las regularizaciones son
 * una lectura aparte (`useStaffRegularizations`): si fallan, el historial se sigue mostrando sin
 * ellas (nunca bloquean la pestaña). Con la empresa suspendida (`/asistencia` usa
 * `allowSuspended`), mismos estados y **sin acciones**: `me` ya está en caché por la puerta, así
 * que se lee sin volver a pedirlo.
 *
 * 008 · T097 (contracts/ui.md §2.2, research R-25/R-26): con horario (`has_schedule`), el bloque
 * plegable "Próximos días" arriba de los días y, en cada día, su estado y turno. Los días evaluados
 * sin marcas (falta, descanso, feriado, vacaciones…) se intercalan por `operative_day`
 * (`mergeScheduleDays`). Es otra lectura aparte (`useStaffDays`): si falla, la pestaña sigue igual
 * sin ella; con la empresa suspendida se ve igual, en solo lectura.
 *
 * 008 · T111 (contracts/ui.md §2.2, spec US7): la sección "Justificaciones" arriba de los días
 * (solo si hay alguna) y, en cada día, "Justificar" o "Justificación en revisión". Otra lectura
 * aparte (`useStaffJustifications`): si falla, la pestaña sigue sin ella.
 */
import { CalendarDays } from 'lucide-react';
import { useMemo } from 'react';

import { useOnlineStatus } from '../../api/online';
import { Button } from '../../components/ui/button';
import { GenericErrorScreen } from '../../components/ui/generic-error';
import { OfflineBanner, OfflineState } from '../../components/ui/offline-state';
import { Screen } from '../../components/ui/screen';
import { Skeleton, SkeletonText } from '../../components/ui/skeleton';
import { TabBar } from '../../components/ui/tab-bar';
import { t } from '../../i18n';
import { useStaffMe } from '../access/api';
import { pendingKindsByDay, useStaffJustifications } from '../justification/api';
import { JustificationsSection } from '../justification/justifications-section';
import { itemsByAnchor, useStaffRegularizations } from '../regularization/api';
import { RegularizationsSection } from '../regularization/regularizations-section';
import { useStaffDays } from '../schedule/api';
import { mergeScheduleDays, scheduleByDay } from '../schedule/format';
import { UpcomingDays } from '../schedule/upcoming-days';

import { useHistory } from './api';
import { DayGroup } from './day-group';

const NAV = <TabBar active="history" />;

/**
 * Skeletons de la pestaña Asistencia. La reutiliza también la puerta de acceso
 * (`routes/asistencia.tsx`) mientras `useAccessGate` resuelve: para el trabajador ambas esperas se
 * ven igual, "cargando mi asistencia".
 */
export function HistoryLoading() {
  return (
    <Screen
      data-testid="history-loading"
      title={t('history.title')}
      lead={t('history.subtitle')}
      nav={NAV}
    >
      <SkeletonText label={t('common.states.loading')} className="gap-fm-5">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} radius="lg" className="h-24 w-full" />
        ))}
      </SkeletonText>
    </Screen>
  );
}

function HistoryEmpty() {
  return (
    <div
      data-testid="history-empty"
      className="flex flex-col items-center gap-fm-3 py-fm-8 text-center"
    >
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-fm-pill bg-fm-primary-soft text-fm-primary-text"
      >
        <CalendarDays size={28} />
      </span>
      <h2 className="font-display text-fm-lg font-bold text-fm-text">{t('history.empty.title')}</h2>
      <p className="text-fm-base text-fm-text-secondary">{t('history.empty.description')}</p>
    </div>
  );
}

export function HistoryScreen() {
  const online = useOnlineStatus();
  const history = useHistory();
  const regularizations = useStaffRegularizations();
  const staffDays = useStaffDays();
  const schedule = useMemo(() => scheduleByDay(staffDays.data), [staffDays.data]);
  const justifications = useStaffJustifications();
  const justificationItems = justifications.data?.items;
  const pendingJustifications = useMemo(
    () => pendingKindsByDay(justificationItems),
    [justificationItems],
  );
  // Solo caché: la puerta de `/asistencia` ya resolvió `GET /me`; aquí no se vuelve a pedir.
  const me = useStaffMe({ enabled: false });
  const readOnly = me.data?.company.status === 'SUSPENDED';
  const regularizationItems = regularizations.data?.items;
  const byAnchor = useMemo(
    () => (regularizationItems !== undefined ? itemsByAnchor(regularizationItems) : undefined),
    [regularizationItems],
  );

  // Sin red y sin nada cargado todavía en esta sesión: no hay qué mostrar (R-20).
  if (!online && history.data === undefined) {
    return (
      <Screen title={t('history.title')} lead={t('history.subtitle')} nav={NAV}>
        <OfflineState onRetry={() => void history.refetch()} retrying={history.isFetching} />
      </Screen>
    );
  }

  if (history.isPending) {
    return <HistoryLoading />;
  }

  if (history.isError) {
    return <GenericErrorScreen nav={NAV} onRetry={() => void history.refetch()} />;
  }

  const historyDays = history.data.pages.flatMap((page) => page.days);
  const today = history.data.pages[0]!.today;
  const days = mergeScheduleDays(historyDays, staffDays.data, {
    oldestLoaded: history.hasNextPage ? (historyDays.at(-1)?.operative_day ?? null) : null,
  });

  return (
    <Screen
      data-testid="history-screen"
      title={t('history.title')}
      lead={t('history.subtitle')}
      nav={NAV}
    >
      {!online ? <OfflineBanner /> : null}

      {regularizationItems !== undefined ? (
        <RegularizationsSection items={regularizationItems} readOnly={readOnly} online={online} />
      ) : null}

      {justificationItems !== undefined ? (
        <JustificationsSection items={justificationItems} readOnly={readOnly} online={online} />
      ) : null}

      {staffDays.data !== undefined ? <UpcomingDays data={staffDays.data} /> : null}

      {days.length === 0 ? (
        <HistoryEmpty />
      ) : (
        <div className="flex flex-col gap-fm-5">
          {days.map((day) => (
            <DayGroup
              key={day.operative_day}
              day={day}
              today={today}
              {...(byAnchor !== undefined ? { regularizations: byAnchor } : {})}
              readOnly={readOnly}
              online={online}
              {...(schedule.has(day.operative_day)
                ? { schedule: schedule.get(day.operative_day)! }
                : {})}
              {...(pendingJustifications.has(day.operative_day)
                ? { pendingJustificationKinds: pendingJustifications.get(day.operative_day)! }
                : {})}
            />
          ))}
        </div>
      )}

      {days.length > 0 && history.hasNextPage ? (
        <Button
          data-testid="history-more"
          variant="secondary"
          onClick={() => void history.fetchNextPage()}
          loading={history.isFetchingNextPage}
          loadingText={t('common.states.loading')}
          disabled={!online}
        >
          {t('history.loadMore')}
        </Button>
      ) : null}

      {days.length > 0 && !history.hasNextPage ? (
        <p data-testid="history-end" className="text-center text-fm-sm text-fm-text-secondary">
          {t('history.end')}
        </p>
      ) : null}
    </Screen>
  );
}
